import { CommonModule } from '@angular/common';
import { Component, computed, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { format } from 'date-fns';
import { AppInfo } from '../../../../global/environment';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { SkeletonLoaderComponent } from '../../../../shared/components/skeleton-loader/skeleton-loader.component';
import { TelephoneLinkComponent } from '../../../../shared/components/telephone-link/telephone-link.component';
import { QMBlockDirective, QMEncryptDirective } from '../../../../shared/directives/quantum-metric.directive';
import { TimeFormatPipe } from '../../../../shared/pipes/time-format.pipe';
import { downloadPDF } from '../../../../shared/utils/download-pdf';
import { capitaliseText } from '../../../../shared/utils/formatting';
import { ContactHoursComponent } from '../../book-claim/components/shared/contact-hours/contact-hours.component';
import { BOOKING_DATA } from '../constants';
import { GAService } from '../service/ga-tags.service';
import { RebookingService } from '../service/rebooking.service';

type Appointment = {
  appointmentDateSelected: string | undefined;
  appointmentSlotSelected: string | null | undefined;
};

@Component({
  selector: 'app-orbit-rebooking-confirmation',
  imports: [
    ButtonComponent,
    TelephoneLinkComponent,
    ContactHoursComponent,
    CommonModule,
    QMBlockDirective,
    SkeletonLoaderComponent,
    QMEncryptDirective,
  ],
  templateUrl: './rebooking-confirmation.component.html',
  styleUrl: './rebooking-confirmation.component.scss',
  providers: [TimeFormatPipe],
})
export class RebookingConfirmationComponent implements OnInit, OnDestroy {
  private _destroyed = false;

  private viewModel = computed(() => {
    const bookingData = this.rebookingService.state.bookingData;
    const appointment = {
      appointmentDateSelected: bookingData?.appointmentDateSelected,
      appointmentSlotSelected: bookingData?.appointmentSlotSelected,
    };

    return {
      bookingData,
      confirmedBookingLoaded: !!bookingData,
      mmrLink: `${AppInfo.Environment.claimsURL}/my-plans/${bookingData?.planNumber}/claims`,
      manufacturer: bookingData?.manufacturer,
      productType: bookingData?.itemType,
      email: bookingData?.customerContactEmail,
      phoneNumber: bookingData?.customerContactMobile || bookingData?.customerHomePhone,
      serviceProvider: bookingData?.serviceProviderName,
      serviceProviderEmail: bookingData?.serviceProviderPublicEmail || bookingData?.serviceProviderContactEmail,
      serviceProviderContactHours: bookingData?.serviceProviderHoursArray,
      serviceProviderTelephone: bookingData?.serviceProviderPhone,
      modelNumber: bookingData?.modelNumber,
      planNumber: bookingData?.planNumber,
      address: [
        ...[bookingData?.customerHouseStreetName, bookingData?.customerLocalArea, bookingData?.customerTownCity].map(
          (i) => i?.toLowerCase()
        ),
        bookingData?.customerPostCode,
      ].filter((v) => !!v && v !== ''),
      bookingSummaryItems: [
        {
          label: 'Engineer visit',
          value: this.formattedAppointmentDate(appointment),
          testId: 'visit-date',
        },
        {
          label: 'Your appliance',
          value: `${bookingData?.manufacturer} ${bookingData?.itemType?.toLowerCase()}`,
          testId: 'appliance-name',
        },
        {
          label: `What's wrong`,
          value: capitaliseText(bookingData?.faultSelected),
          testId: 'fault-name',
        },
        {
          label: 'Model number',
          value: bookingData?.modelNumber,
          testId: 'model-number',
        },
        {
          label: 'Plan number',
          value: bookingData?.planNumber,
          testId: 'plan-number',
          encrypt: true,
        },
        {
          label: 'Reference',
          value: bookingData?.serviceProviderBookingRef || this.rebookingService.state.claimId,
          testId: 'reference-number',
          encrypt: true,
        },
      ].filter((i) => i.value && i.value !== ''),
      appointmentTime: this.formattedAppointmentTime(appointment),
    };
  });

  get vm() {
    return this.viewModel();
  }

  constructor(
    private readonly rebookingService: RebookingService,
    private readonly gaService: GAService,
    private readonly route: ActivatedRoute
  ) {}

  ngOnDestroy(): void {
    this._destroyed = true;
  }

  ngOnInit() {
    const claimId = this.route.snapshot.params['claimId'];
    const planNumber = this.route.snapshot.params['planNumber'];
    this.rebookingService.claimId.set(claimId);
    this.rebookingService.planNumber.set(planNumber);
    if (claimId) {
      this.rebookingService.claimId.set(claimId);
    }

    const bookingData = sessionStorage.getItem(BOOKING_DATA);

    if (bookingData) {
      const data = JSON.parse(bookingData);
      this.rebookingService.bookingData.set(data);
      this.sendPageLoadGATags();
    } else {
      this.rebookingService
        .getRepairData(this.rebookingService.state.claimId)
        .subscribe({ next: () => this.sendPageLoadGATags() });
    }
  }

  clearConfirmedBooking(): void {
    sessionStorage.removeItem('confirmedBooking');
  }

  formattedAppointmentDate(appointment: Appointment): string | undefined {
    if (!appointment) return;
    const dateValue = appointment.appointmentDateSelected;
    if (!dateValue) return;
    const apptDate = format(dateValue, 'do MMMM yyyy');
    const dayOfWeek = format(dateValue, 'EEEE');
    const time = this.formattedAppointmentTime(appointment);
    return `${dayOfWeek} ${apptDate}${time ? ` at ${time}` : ''}`;
  }

  formattedAppointmentTime(appointment: Appointment): string {
    const slot = appointment.appointmentSlotSelected;
    if (slot && slot !== 'ANY') {
      return `${appointment.appointmentSlotSelected}`;
    }
    return '';
  }

  handleDownload(): void {
    downloadPDF();
  }

  trackClick() {
    this.gaService.sendGATag({
      ...this.gaService.GA_CONFIRMATION.MMR(),
      claimedTimeSlot: this.vm?.appointmentTime || '',
      repairer: this.vm?.serviceProvider || '',
    });
  }

  sendPageLoadGATags() {
    this.gaService.sendGATag({
      ...this.gaService.GA_CONFIRMATION.PAGE_LOAD(),
      claimedTimeSlot: this.vm?.appointmentTime || '',
      repairer: this.vm?.serviceProvider || '',
    });
    this.gaService.sendGATag({
      ...this.gaService.GA_CONFIRMATION.CONFIRMATION_EVENT(),
      claimedTimeSlot: this.vm?.appointmentTime || '',
      repairer: this.vm?.serviceProvider || '',
    });
  }
}
